import { computed, queryDb } from "@livestore/livestore";
import type { Queryable, Store } from "@livestore/livestore";
import { uiState$ } from "./livestore/queries.ts";
import { events, tables } from "./livestore/schema.ts";
import type { AutocompleteVM } from "./types.ts";

export function createAutocompleteScope(store: Store): AutocompleteVM {
  const inputText$ = computed((query) => query(uiState$).inputText, {
    label: "inputText",
  });
  const selectedIndex$ = computed((query) => query(uiState$).selectedIndex, {
    label: "selectedIndex",
  });

  // Extract filter text from input (everything after @)
  const filterText$ = computed(
    (query) => {
      const text = query(inputText$);
      const atIndex = text.lastIndexOf("@");
      if (atIndex === -1) return "";
      return text.slice(atIndex + 1).toLowerCase();
    },
    { label: "filterText" },
  );

  // Filtered and sorted files
  const filteredFiles$ = computed(
    (query) => {
      const filter = query(filterText$);
      if (!filter) return [];

      const allFiles = query(queryDb(() => tables.files, { label: "allFiles" }));

      return allFiles
        .filter((file) => file.path.toLowerCase().includes(filter))
        .sort((a, b) => {
          // Sort by how early the match appears
          const aIndex = a.path.toLowerCase().indexOf(filter);
          const bIndex = b.path.toLowerCase().indexOf(filter);
          if (aIndex !== bIndex) return aIndex - bIndex;
          return a.path.localeCompare(b.path);
        })
        .slice(0, 10); // Limit to 10 results
    },
    { label: "filteredFiles" },
  );

  const options$ = computed(
    (query) => {
      const files = query(filteredFiles$);
      return files.map(
        (
          file,
          idx,
        ): {
          key: string;
          label: string;
          selected$: Queryable<boolean>;
          click(): void;
        } => ({
          key: file.path,
          label: file.path,
          selected$: computed((get) => idx === get(selectedIndex$), {
            label: `option-${idx}-selected`,
            deps: [idx],
          }),
          click: () => {
            const currentText = store.query(inputText$);
            const atIndex = currentText.lastIndexOf("@");
            if (atIndex !== -1) {
              const newText = currentText.slice(0, atIndex + 1) + file.path + " ";
              store.commit(events.uiStateSet({ inputText: newText, selectedIndex: 0 }));
            }
          },
        }),
      );
    },
    { label: "options" },
  );

  return {
    inputText$,
    updateInput: (text: string, pos: "end" | number) => {
      store.commit(events.uiStateSet({ inputText: text, selectedIndex: 0 }));
    },
    updateSelection: (pos: number, anchor?: number) => {
      // For now, selection dismisses the autocomplete
    },
    pressKey: (key: "enter" | "up" | "down" | "escape") => {
      const options = store.query(options$);
      if (options.length === 0) return;

      const currentIdx = store.query(selectedIndex$);

      switch (key) {
        case "enter": {
          const selected = options[currentIdx];
          if (selected) {
            selected.click();
          }
          break;
        }
        case "up": {
          const newIdx = Math.max(0, currentIdx - 1);
          store.commit(events.uiStateSet({ selectedIndex: newIdx }));
          break;
        }
        case "down": {
          const newIdx = Math.min(options.length - 1, currentIdx + 1);
          store.commit(events.uiStateSet({ selectedIndex: newIdx }));
          break;
        }
        case "escape": {
          store.commit(events.uiStateSet({ inputText: "", selectedIndex: 0 }));
          break;
        }
      }
    },
    options$,
  };
}
